import { formatISO } from 'date-fns'
import { NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import debounce from 'lodash.debounce'
import uniqBy from 'lodash.uniqby'

import { setSpaceRedirectUrl } from 'lib/setSpaceRedirectUrl'

import useCheckBreakpoint from 'lib/checkBreakpoint'
import Meta from 'lib/meta'

import Layout from 'components/commons/layouts/Layout'
import SearchFilterModal from 'components/domains/spaces/SearchFilterModal'
import SearchForm from 'components/domains/spaces/SearchForm'
import LocationUnitCardList from 'components/domains/locationUnit/LocationUnitCardList'
import LocationUnitGoogleMap from 'components/domains/locationUnit/LocationUnitGoogleMap'

import { browserName, osName } from 'lib/checkDevice'
import { StoreState } from 'state/ducks'
import { isLoading as getIsLoading } from 'state/ducks/app/selectors'
import { fetchLocationUnitsRequest } from 'state/ducks/locationUnit/actions'
import { Filter } from 'state/ducks/space/models'
import { useGoogleMapLogger } from 'hooks/logger/useGoogleMapLogger'
import { getLocationUnits } from 'state/ducks/locationUnit/selectors'
import { getCoordinate, LocationUnit, LocationUnitId } from 'state/ducks/locationUnit/models'

import styles from './LocationsPage.module.scss'
import moment from 'moment'

const DEFAULT_LAT = 35.681236
const DEFAULT_LON = 139.767125

type LocationsPageProps = {
  locale: string
}

const LocationsPage: NextPage<LocationsPageProps> = (props): JSX.Element => {
  const { query, asPath, locale, isReady } = useRouter()
  const mapRef = useRef(null)

  const dispatch = useDispatch()
  const searchResult = useSelector((state: StoreState) => getLocationUnits(state))
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions()
  const isLoading = useSelector(getIsLoading)
  const locationUnits = searchResult.locationUnits

  const { isDesktop, isMobile, isTablet } = useCheckBreakpoint()
  const { t } = useTranslation()

  const [isFiltered, SetIsFiltered] = useState<boolean>(false)
  const [isVisibleSearchFilterModal, setIsVisibleSearchFilterModal] = useState<boolean>(false)
  const [isVisibleSpaceCardList, setIsVisibleSpaceCardList] = useState<boolean>(false)
  const [searchParams, setSearchParams] = useState<Filter>({
    lat: 35.681236,
    lon: 139.767125,
    available_24_hour: false,
    can_book_on_the_day: false,
  })
  const [zoomLabel, setZoomLabel] = useState<number>(15)
  const [checkedFilterItems, setCheckedFilterItems] = useState<object>({})
  const [selectedLocationUnitId, setSelectedLocationUnitId] = useState<LocationUnitId>({
    type: '',
    encryptedId: '',
  })
  const [locationName, setLocationName] = useState<string>('')
  const { logGoogleMapUsage } = useGoogleMapLogger()

  const [units, setUnits] = useState<LocationUnit[]>([])

  useEffect(() => {
    let _units: LocationUnit[] = [...units]

    if (_units.length >= 250) {
      setUnits(locationUnits)
    } else {
      _units = [...locationUnits, ..._units]

      _units = uniqBy(_units, (unit) => `${unit.encryptedId}-${unit.type}`)

      setUnits(_units)
    }
  }, [locationUnits])

  const geolocationOptions = {
    enableHighAccuracy: true,
    timeout: 100000,
    maximumAge: 5000,
  }

  const initialLocation = useMemo(() => {
    return {
      lat: query?.lat && Number(query.lat) ? Number(query.lat) : DEFAULT_LAT,
      lon: query?.lon && Number(query.lon) ? Number(query.lon) : DEFAULT_LON,
    }
  }, [query?.lat, query?.lon])

  const _initGoogleMap = useCallback(
    (map) => {
      mapRef.current = map
      map.setCenter({
        lat: initialLocation.lat,
        lng: initialLocation.lon,
      })
      logGoogleMapUsage({
        timestamp: new Date().toISOString(),
        screen: 'locations',
        env: process.env.SENTRY_ENVIRONMENT || 'staging',
        platform: 'web',
        action_type: 'load_map',
        api_sku: 'Dynamic Maps',
        latitude: Number(searchParams?.lat) || 0,
        longitude: Number(searchParams?.lon) || 0,
        user_input_text: String(query?.name || ''),
      })
    },
    [logGoogleMapUsage, query?.name, searchParams?.lat, searchParams?.lon]
  )

  const _initPageScrollPos = () => {
    //スクロールの初期化
    const pos = Math.max(
      window.pageYOffset,
      document.documentElement.scrollTop,
      document.body.scrollTop
    )
    if (pos > 0) {
      document.querySelector('body').scrollTo(0, 0)
    }
  }

  //初期化処理
  useEffect(() => {
    _initPageScrollPos()

    setSpaceRedirectUrl()
  }, [])

  useEffect(() => {
    if (!isReady) {
      return
    }

    const convertDateTime = (date: string, hour: string, minutes: string) => {
      if (!date || !hour || !minutes) {
        return undefined
      }

      return moment(`${date} ${hour}:${minutes}`, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DDTHH:mm:ssZ')
    }

    let _searchParams: any = {
      lat: query?.lat && Number(query.lat) ? Number(query.lat) : DEFAULT_LAT,
      lon: query?.lon && Number(query.lon) ? Number(query.lon) : DEFAULT_LON,
      booked_from: convertDateTime(
        String(query?.startDate || ''),
        String(query?.startDateTimeHour || ''),
        String(query?.startDateTimeMin || '')
      ),
      booked_to: convertDateTime(
        String(query?.endDate || ''),
        String(query?.endDateTimeHour || ''),
        String(query?.endDateTimeMin || '')
      ),
      bag_size: query?.bagSize ? Number(query.bagSize) : 0,
      suitcase_size: query?.suitcaseSize ? Number(query.suitcaseSize) : 0,
    }

    dispatch(
      fetchLocationUnitsRequest({
        time_zone: timeZone,
        os_name: osName,
        browser_name: browserName,
        locale: locale,
        ..._searchParams,
      })
    )

    setSearchParams(_searchParams)
  }, [locale, dispatch, timeZone, query, isReady])

  const _onChangeZoomMap = useCallback((): void => {
    const zoom = mapRef.current ? mapRef.current.zoom : zoomLabel
    setZoomLabel(zoom)

    // Reset marker when dragging Map
    setSelectedLocationUnitId({ type: '', encryptedId: '' })
  }, [])

  const _onDragMap = useCallback((): void => {
    const lat = mapRef.current.center.lat()
    const lng = mapRef.current.center.lng()

    const _searchParams = {
      ...searchParams,
      lat: lat,
      lon: lng,
    }

    setSearchParams(_searchParams)

    dispatch(
      fetchLocationUnitsRequest({
        time_zone: timeZone,
        os_name: osName,
        browser_name: browserName,
        locale: locale,
        ..._searchParams,
        ...checkedFilterItems,
      })
    )

    // Reset marker when dragging Map
    setSelectedLocationUnitId({ type: '', encryptedId: '' })
    setLocationName(t('SPACES.HEADING.DEFAULT_PREFIX_TITLE'))
  }, [checkedFilterItems, dispatch, locale, searchParams, t, timeZone])

  const debouncedOnDragMap = useMemo(() => {
    return debounce(_onDragMap, 500)
  }, [_onDragMap])

  useEffect(() => {
    return () => {
      debouncedOnDragMap.cancel()
    }
  }, [debouncedOnDragMap])

  const itemRefs = useRef<(HTMLLIElement | null)[]>([])

  const _onPressMarker = useCallback(
    (locationUnit: LocationUnit): void => {
      setSelectedLocationUnitId({ type: locationUnit.type, encryptedId: locationUnit.encryptedId })

      if (isDesktop) {
        const selectedIndex = units.findIndex(
          (item) => item.type === locationUnit.type && item.encryptedId === locationUnit.encryptedId
        )

        if (itemRefs.current[selectedIndex]) {
          itemRefs.current[selectedIndex].scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }

      const coordinate = getCoordinate(locationUnit)

      setSearchParams({
        ...searchParams,
        lat: coordinate.latitude,
        lon: coordinate.longitude,
      })

      const center = new window.google.maps.LatLng(
        Number(coordinate.latitude),
        Number(coordinate.longitude)
      )
      mapRef.current.panTo(center)
    },
    [searchParams, units]
  )

  const _selectSearchParameters = useCallback(
    (checked: boolean, id: string): void => {
      setCheckedFilterItems({
        ...checkedFilterItems,
        [id]: checked,
      })
    },
    [checkedFilterItems]
  )

  const _onPressSearchFilter = useCallback((): void => {
    const date = new Date(new Date())
    const _searchParams = {
      ...searchParams,
      ...checkedFilterItems,
      booked_from: searchParams.booked_from || formatISO(date.setHours(10)),
      booked_to: searchParams.booked_to || formatISO(date.setHours(19)),
      time_zone: timeZone,
      os_name: osName,
      browser_name: browserName,
      locale: locale,
    }

    setSearchParams(_searchParams)
    dispatch(fetchLocationUnitsRequest(_searchParams))
    setIsVisibleSearchFilterModal(false)

    // update filtered
    SetIsFiltered(
      Object.keys(checkedFilterItems).length > 0 &&
        Object.values(checkedFilterItems).some((value) => value === true)
    )
  }, [checkedFilterItems, dispatch, locale, searchParams, timeZone])

  const _onPressSearchSpace = useCallback(
    (latLng, selectedDate, selectedDateTime, bagSize, suitcaseSize): void => {
      const [startHour, startMinute] = selectedDateTime.startDateTime.split(':').map(Number)
      const [endHour, endMinute] = selectedDateTime.endDateTime.split(':').map(Number)

      const startDate = moment(selectedDate.startDate || new Date())
        .set({ hour: startHour, minute: startMinute, second: 0 })
        .toDate()
      const endDate = moment(selectedDate.endDate || new Date())
        .set({ hour: endHour, minute: endMinute, second: 0 })
        .toDate()

      const _searchParams: Filter = {
        lat: latLng.lat || mapRef.current.center.lat(),
        lon: latLng.lon || mapRef.current.center.lng(),
        booked_from: formatISO(startDate),
        booked_to: formatISO(endDate),
        bag_size: bagSize,
        suitcase_size: suitcaseSize,
        available_24_hour: false,
        can_book_on_the_day: false,
      }

      mapRef.current.panTo({
        lat: _searchParams.lat,
        lng: _searchParams.lon,
      })

      setSearchParams(_searchParams)
      setUnits([])
      dispatch(
        fetchLocationUnitsRequest({
          time_zone: timeZone,
          os_name: osName,
          browser_name: browserName,
          locale: locale,
          ..._searchParams,
          ...checkedFilterItems,
        } as any)
      )
      setLocationName(t('SPACES.HEADING.DEFAULT_PREFIX_TITLE'))
    },
    [checkedFilterItems, dispatch, t, timeZone, locale]
  )

  return (
    <Layout>
      <>
        <Meta
          title={`${t('META.TITLE.SPACE_PAGE_PREFIX', {
            locationName: query.name || t('SPACES.HEADING.DEFAULT_PREFIX_TITLE'),
          })}${t('META.TITLE.SPACE_PAGE')}`}
          description={`${t('META.TITLE.SPACE_PAGE_PREFIX', {
            locationName: query.name || t('SPACES.HEADING.DEFAULT_PREFIX_TITLE'),
          })}${t('META.DESCRIPTION.SPACE_PAGE')}`}
          url={`/${props.locale === 'default' ? 'ja' : props.locale}${asPath}`}
          isCrawlingNoIndex={true}
        />
        {isTablet || isMobile ? (
          <div className={styles.spaceContainer}>
            <div className={styles.contents}>
              <div className={styles.map}>
                {isVisibleSpaceCardList && (
                  <div className={styles.spaceCardList}>
                    <LocationUnitCardList
                      onPressSearchFilterButton={() =>
                        setIsVisibleSearchFilterModal(!isVisibleSearchFilterModal)
                      }
                      locationUnits={units}
                      selectedLocationUnitId={selectedLocationUnitId}
                      locationName={locationName}
                      isLoading={isLoading}
                      isFiltered={isFiltered}
                    />
                  </div>
                )}

                <div className={styles.mapContainer}>
                  <LocationUnitGoogleMap
                    zoomLabel={zoomLabel}
                    locationUnits={units}
                    mapRef={mapRef}
                    initGoogleMap={_initGoogleMap}
                    onDragMap={debouncedOnDragMap}
                    onChangeZoomMap={_onChangeZoomMap}
                    onPressMarker={_onPressMarker}
                    selectedLocationUnitId={selectedLocationUnitId}
                  />
                </div>
                <div className={styles.bottomButtons}>
                  <button
                    className={styles.bottomButton}
                    type="button"
                    onClick={() => {
                      setIsVisibleSpaceCardList(!isVisibleSpaceCardList)
                    }}
                  >
                    {isVisibleSpaceCardList ? (
                      <>
                        <img src="/switching_map_icon.svg" alt={t('ALT.SWITCHING_MAP_ICON')} />
                        {t('SPACES.BUTTONS.SWITCHING_MAP')}
                      </>
                    ) : (
                      <>
                        <img src="/switching_list_icon.svg" alt={t('ALT.SWITCHING_LIST_ICON')} />
                        {t('SPACES.BUTTONS.SWITCHING_LIST')}
                      </>
                    )}
                  </button>
                  <button
                    className={`${styles.bottomButton} ${styles.bottomButtonFilter} ${
                      isFiltered ? styles.bottomButtonFilterActive : ''
                    }`}
                    type="button"
                    onClick={() => setIsVisibleSearchFilterModal(!isVisibleSearchFilterModal)}
                  >
                    {isFiltered ? t('COMMON.FILTERING') : t('COMMON.FILTER')}
                  </button>
                </div>
              </div>
            </div>
            <div className={styles.spaceSearchForm}>
              <SearchForm
                page="locations"
                onPressSearchSpace={_onPressSearchSpace}
                setSearchParams={({ lat, lon }) => {
                  setSearchParams((prev) => ({ ...prev, lat, lon }))
                }}
                isVisibleFilterButton={true}
                onPressSearchFilterModal={() => {
                  setIsVisibleSearchFilterModal(!isVisibleSearchFilterModal)
                }}
                locale={locale}
              />
            </div>
          </div>
        ) : (
          <div className={styles.spaceContainer}>
            <div className={styles.contents}>
              <div className={styles.mainBlock}>
                <SearchForm
                  page="locations"
                  onPressSearchSpace={_onPressSearchSpace}
                  setSearchParams={({ lat, lon }) => {
                    setSearchParams((prev) => ({ ...prev, lat, lon }))
                  }}
                />
                <div className={styles.spaceCardList}>
                  <LocationUnitCardList
                    onPressSearchFilterButton={() =>
                      setIsVisibleSearchFilterModal(!isVisibleSearchFilterModal)
                    }
                    locationUnits={units}
                    selectedLocationUnitId={selectedLocationUnitId}
                    locationName={locationName}
                    isLoading={isLoading}
                    itemRefs={itemRefs}
                    isFiltered={isFiltered}
                  />
                </div>
              </div>
              <div className={styles.map}>
                <LocationUnitGoogleMap
                  zoomLabel={zoomLabel}
                  locationUnits={units}
                  mapRef={mapRef}
                  initGoogleMap={_initGoogleMap}
                  onDragMap={debouncedOnDragMap}
                  onChangeZoomMap={_onChangeZoomMap}
                  onPressMarker={_onPressMarker}
                  selectedLocationUnitId={selectedLocationUnitId}
                />
              </div>
            </div>
          </div>
        )}

        {isVisibleSearchFilterModal && (
          <SearchFilterModal
            isVisibleSearchFilterModal={isVisibleSearchFilterModal}
            onPressCloseModalRequest={() => setIsVisibleSearchFilterModal(false)}
            checkedFilterItems={checkedFilterItems}
            selectSearchParameters={_selectSearchParameters}
            onPressSearchFilter={_onPressSearchFilter}
          />
        )}
      </>
    </Layout>
  )
}

export async function getServerSideProps({ locale }) {
  return {
    props: {
      locale: locale.replace('-', '_').toLowerCase(),
      ...(await serverSideTranslations(locale, ['common'])),
    },
  }
}

export default LocationsPage
